<template>
	<TheStdLayout :title="$tc('Settings', 2)">

		<EvaluationLevelEditor
			v-if="showLevelEditor"
			@requestClose="showLevelEditor = false"
		/>

		<div class="px-4 py-6 mx-auto sm:px-6 md:px-8">

			<div class="settings-group">
				<h2>{{ $t('Account') }}</h2>
				<div class="setting">
					<div class="setting-explanation">
						<h3>{{ $t('Subscription plan') }}</h3>
						<p>{{ $t('Your current Smart Symbols subscription plan') }}</p>
					</div>
					<div class="setting-control">
						<span class="capitalize">{{ subscriptionPlan }}</span>

						<div v-if="trialExpirationDate" class="mt-2">
							<div class="alert alert-warning">
								<div>
									<p class="font-bold">
										{{ $t('Trial ends on {date}.', { date: trialExpirationDate?.toLocaleString() } ) }}
									</p>
									<span>{{ $t('Convinced of Smart Symbols and want to continue using it?') }}&nbsp;</span>
									<span v-html="$t('Contact us at {email}.', {
										email: '<b><a href=&quot;mailto:' + salesEmail + '&quot; target=&quot;_blank&quot;>' + salesEmail + '</a></b>'
									})"></span>
								</div>
							</div>
						</div>
					</div>
				</div>
				<!-- <div class="setting">
					<div class="setting-explanation">
						<h3>{{ $t('Licenses') }}</h3>
						<p>{{ $t('Manage the number of pupils that can be actively followed up in Smart Symbols') }}</p>
					</div>
					<div class="setting-control">
						??
					</div>
				</div>
				<div class="setting">
					<div class="setting-explanation">
						<h3>{{ $t('Billing information') }}</h3>
						<p>{{ $t('Manage your billing information') }}</p>
					</div>
					<div class="setting-control">
						[Name]<br>
						[Address]<br>
						[Zipcode + City]
					</div>
				</div> -->

			</div>

			<div class="settings-group">
				<h2>{{ $t('Pupil follow-up') }}</h2>
				<div class="setting">
					<div class="setting-explanation">
						<h3 class="flex items-center gap-2">
							{{ $t('Curriculum') }}
							<button type="button" class="btn-help" @click="showCurriculumInfo = !showCurriculumInfo" />
						</h3>
						<p class="flex items-center gap-1">
							{{ $t('The set curriculum for this account') }}
						</p>
					</div>
					<div class="setting-control">
						{{ curriculumName ?? '?' }}
					</div>
				</div>
				<div class="setting" v-if="organisationsStore.getCurrentOrganisation().curriculum">
					<div class="setting-explanation">
						<h3 class="flex items-center gap-2">
							{{ $t('Evaluation levels') }}
							<button type="button" class="btn-help" @click="showEvaluationLevelInfo = !showEvaluationLevelInfo" />
						</h3>
						<p>{{ $t('Manage the levels to evaluate pupils') }}</p>
					</div>
					<div class="setting-control">

						<div v-if="evaluationLevelsStore.loading">
							<div class="flex justify-center py-12">
								<span class="loading loading-spinner loading-md"></span>
							</div>
						</div>

						<div v-else>
							<!-- Default values:
								- {{ $t('#evaluation level: Achieving expectations') }},
								- {{ $t('#evaluation level: Beyond expectations') }},
								- {{ $t('#evaluation level: Continue practicing') }},
								- {{ $t('#evaluation level: Instructions needed') }}
								DO NOT REMOVE AS IT IS USED FOR TRANSLATION EXTRACTION
							-->

							<div class="flex flex-col gap-2 max-w-xs relative">
								<div class="flex flex-col gap-1">
									<div class="flex gap-4 items-center" v-for="level in evaluationLevelsStore.achievedLevels" :key="level.id">
										<div class="w-5 h-5 rounded-full" :style="{ backgroundColor: level.color }"></div>
										{{ level.isPredefinedLevel? $t(level.nameTranslationKey) : level.name }}
									</div>
								</div>
								<div class="h-1 bg-base-100"></div>
								<div class="flex flex-col gap-1">
									<div class="flex gap-4 items-center" v-for="level in evaluationLevelsStore.unachievedLevels" :key="level.id">
										<div class="w-5 h-5 rounded-full" :style="{ backgroundColor: level.color }"></div>
										{{ level.isPredefinedLevel? $t(level.nameTranslationKey) : level.name }}
									</div>
								</div>

								<div class="absolute -top-3 right-0" v-if="canEditEvaluationLevels">
									<button type="button" class="btn btn-circle btn-ghost bg-base-100" @click="showLevelEditor = true">
										<PencilIcon class="w-5 h-5"></PencilIcon>
									</button>
								</div>
							</div>
						</div>

					</div>
				</div>
				<div class="setting">
					<div class="setting-explanation">
						<h3 class="flex items-center gap-2">
							{{ $t('Attach a cloud file or folder as pupil file') }}
							<button type="button" class="btn-help" @click="showPupilCloudLinkInfo = !showPupilCloudLinkInfo" />
						</h3>
						<p class="flex items-center gap-1">
							{{ $t('Allow to link a cloud file or folder to each pupil, so that external files (like a care file) can be easily linked to their profile.') }}
						</p>
					</div>
					<div class="setting-control">
						<SwitchRoot
							v-model="pupilCloudLinkEnabled"
							@update:modelValue="onPupilCloudLinkSettingChanged"
							class="w-8 h-5 shadow-sm flex data-[state=unchecked]:bg-gray-300 data-[state=checked]:bg-gray-800 border border-gray-300 data-[state=checked]:border-stone-700 rounded-full relative transition-[background]"
						>
							<SwitchThumb
								class="w-3.5 h-3.5 my-auto bg-white text-xs flex items-center justify-center shadow-xl rounded-full transition-transform translate-x-0.5 will-change-transform data-[state=checked]:translate-x-full"
							/>
						</SwitchRoot>
					</div>
				</div>
			</div>

		</div>

	<KBView v-if="showCurriculumInfo" :url="{
		en: 'https://handsomely-cicada-2cb.notion.site/ebd/21f0964c114280e79774d872429f6149',
		nl: 'https://handsomely-cicada-2cb.notion.site/ebd/2770964c114280f6a0d6e9d572f3a1f4',
	}" @requestClose="showCurriculumInfo = false" />

	<KBView v-if="showEvaluationLevelInfo" :url="{
		en: 'https://handsomely-cicada-2cb.notion.site/ebd/26c0964c114280f19c27f43a9ffd12a3',
		nl: 'https://handsomely-cicada-2cb.notion.site/ebd/2860964c1142804880d8cc423438c262',
	}" @requestClose="showEvaluationLevelInfo = false" />

	<KBView v-if="showPupilCloudLinkInfo" :url="{
		en: 'https://handsomely-cicada-2cb.notion.site/ebd/34a0964c114280bfb7e4d6a14b57c6c3',
		nl: 'https://handsomely-cicada-2cb.notion.site/ebd/34a0964c114280ad8f22e5219be25ced',
	}" @requestClose="showPupilCloudLinkInfo = false" />

	</TheStdLayout>
</template>

<script lang="ts">
import { mapStores } from 'pinia';
import { useEvaluationLevelsStore } from '@/stores/EvaluationLevels.store';
import { useOrganisationsStore } from '@/stores/Organisations.store';
import { GoalSource } from '@/utils/GoalSource';
import EvaluationLevelEditor from '@/components/EvaluationLevelEditor.vue';
import { PencilIcon, QuestionMarkCircleIcon } from '@heroicons/vue/24/outline';
import { SwitchRoot, SwitchThumb } from 'reka-ui';


export default {
	components: {
    	EvaluationLevelEditor,
		PencilIcon, QuestionMarkCircleIcon,
		SwitchRoot, SwitchThumb
  	},

	data() {
		return {
			showLevelEditor: false,
			curriculumName: null as string | null,
			showCurriculumInfo: false,
			showEvaluationLevelInfo: false,
			showPupilCloudLinkInfo: false,
			pupilCloudLinkEnabled: false,
			syncTimeoutId: null as number | null,
		};
	},

	computed: {
		...mapStores(
			useEvaluationLevelsStore,
			useOrganisationsStore,
		),

		canEditEvaluationLevels(): boolean {
			// Placeholder logic for permission check
			// Replace with actual permission logic as needed
			return true; // Assume user has permission for demonstration
		},

		subscriptionPlan(): string {
			return this.organisationsStore.getCurrentOrganisation().subscription_plan ?? '?';
		},

		isTrialAboutToExpire(): boolean {
			return this.organisationsStore.getCurrentOrganisation().trialAboutToExpire;
		},

		trialExpirationDate(): Date | undefined {
			return this.organisationsStore.getCurrentOrganisation().termination_date;
		},

		salesEmail(): string {
			return this.organisationsStore.getCurrentOrganisation().salesEmail;
		},
	},

	async mounted() {

		this.pupilCloudLinkEnabled = this.organisationsStore.getCurrentOrganisation().pupil_cloud_link_enabled ?? false;

		const curriculum = this.organisationsStore.getCurrentOrganisation().curriculum;
		if (!curriculum) {
			return;
		}

		const goalSource = new GoalSource();
		await Promise.all([
			this.evaluationLevelsStore.load(),
			goalSource.load(curriculum)
		]);

		if (goalSource.getName()) {
			this.curriculumName = goalSource.getName();
		} else {
			this.curriculumName = this.organisationsStore.getCurrentOrganisation().curriculum?.name;
		}

	},

	methods: {
		onPupilCloudLinkSettingChanged(newValue: boolean) {
			// save the new value to the store (and backend), but with a short delay to prevent too many requests when toggling quickly
			if (this.syncTimeoutId) {
				clearTimeout(this.syncTimeoutId);
			}
			if(newValue !== this.organisationsStore.getCurrentOrganisation().pupil_cloud_link_enabled) {
				this.syncTimeoutId = setTimeout(() => {
					this.organisationsStore.getCurrentOrganisation().pupil_cloud_link_enabled = newValue;
					this.organisationsStore.save(this.organisationsStore.getCurrentOrganisation());
				}, 500);
			}
		},
	}
}
</script>
